/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 07-07-2026
 *
 * Gedeelde highlight-logica voor node-gebonden overlays (normfeedback/simfeedback/bouwsuggestie):
 * zet de lichte `node-dom-highlight`-class aan of uit op de node, en bij een relatie ook op de
 * gekoppelde edges. Houdt via `node.scratch('node-dom-highlighter')` bij wie de eigenaar is, zodat
 * twee gelijktijdig actieve overlays op dezelfde node elkaars highlight niet wegvegen.
 *
 * Daarnaast (golf 3b) de zwaardere feedback-highlight `highlighted`, die de balk-selectie op het
 * canvas aanwijst. Andere klasse, andere eigenaar: die kent er maar één tegelijk, dus daar hoort
 * geen eigenaarsadministratie bij — de `FeedbackSelectieService` wist en zet hem als geheel.
 */
import type {Core, NodeSingular, SingularElementArgument} from 'cytoscape';
import {elements} from 'dynalearn-common/lib/elements';

export function syncNodeHighlight(node: NodeSingular, cy: Core, owner: unknown, aan: boolean): void {
    const nodesTodo: SingularElementArgument[] = [node];
    if (elements.isSubtypeOf(node.data('type'), 'relation')) {
        nodesTodo.push(
            ...cy.$(`[orig_source='${node.id()}']`).toArray(),
            ...cy.$(`[orig_target='${node.id()}']`).toArray()
        );
    }
    for (const el of nodesTodo) {
        const nodeHighlighted = el.hasClass('node-dom-highlight');
        if (nodeHighlighted !== aan) {
            if (!aan && el.scratch('node-dom-highlighter') === owner) {
                el.removeClass('node-dom-highlight');
                el.scratch('node-dom-highlighter', null);
            } else if (aan) {
                el.addClass('node-dom-highlight');
                el.scratch('node-dom-highlighter', owner);
            }
        }
    }
}

/**
 * Highlight de opgegeven nodes met de feedback-highlight, en bij een relatie ook de edges die er
 * bij horen (die dragen `orig_source`/`orig_target`). Port van ng1's `highlightNodesAlgemeen`;
 * bestaande highlights blijven staan, wissen doet `wisFeedbackHighlight`.
 */
export function highlightNodes(cy: Core, ids: string[]): void {
    for (const id of ids.filter(Boolean)) {
        const node = cy.$id(id);
        if (node.empty()) {
            continue;
        }
        if (elements.isSubtypeOf(node.data('type'), 'relation')) {
            cy.$(`[orig_source='${id}'],[orig_target='${id}']`).addClass('highlighted');
        }
        node.addClass('highlighted');
    }
}

/**
 * Wist de feedback-highlight van alle elementen op het canvas.
 */
export function wisFeedbackHighlight(cy: Core): void {
    cy.$('.highlighted').removeClass('highlighted');
}
